import { Clock, FileText, MapPin, Package, User } from 'lucide-react';
import type { StockUsageRow, Unit } from '@eombor/contracts';
import { Button, Dialog } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { unitText } from '../../../shared/i18n/labels.js';

/**
 * BITTA OLISHNING kartochkasi.
 *
 * Lentadagi yozuv qisqa bo'lishi kerak — mas'ul u yerga «bugun kim nima
 * oldi?» degan savol bilan keladi va o'nlab satrni ko'zdan kechiradi.
 * Ammo bitta yozuv ustida to'xtalganda savol o'zgaradi: «bu aynan qaysi
 * javondan, qaysi qog'oz bilan kelgan mahsulot va xodim nima deb
 * yozgan?». Lentaga bu javoblarni sig'dirish uni o'qib bo'lmaydigan
 * qilardi, shuning uchun ular shu yerda — bosilgandan keyin.
 *
 * Oyna FAQAT o'qish uchun: olingan mahsulotni qaytarib bo'lmaydi, jurnal
 * esa o'zgarmas.
 */
const pad = (n: number): string => String(n).padStart(2, '0');

const momentText = (iso: string): string => {
  const d = new Date(iso);
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const qtyText = (qty: number): string => (Math.round(qty * 1000) / 1000).toLocaleString('ru-RU');

export const UsageDetailDialog = ({
  row,
  onClose,
  onOpenRequisition,
}: {
  row: StockUsageRow;
  onClose: () => void;
  onOpenRequisition?: (id: string) => void;
}) => (
  <Dialog
    open
    onClose={onClose}
    title={row.nomenclatureName}
    description={t('usage.detailHint')}
    footer={
      <div className="flex w-full items-center justify-end">
        <Button variant="ghost" onClick={onClose}>
          {t('usage.detailClose')}
        </Button>
      </div>
    }
  >
    <div className="flex flex-col gap-4">
      {/* Miqdor BIRINCHI va kattaroq: kartochkaning bosh savoli shu. */}
      <p className="tabular text-2xl font-semibold text-warn">
        −{qtyText(row.qty)} {unitText(row.unit as Unit)}
      </p>

      <dl className="flex flex-col gap-3">
        <Line icon={<User size={14} aria-hidden />} label={t('usage.detailWorker')}>
          {row.workerName}
        </Line>
        <Line icon={<Clock size={14} aria-hidden />} label={t('usage.detailWhen')}>
          <span className="tabular">{momentText(row.usedAt)}</span>
        </Line>
        <Line icon={<Package size={14} aria-hidden />} label={t('usage.detailProduct')}>
          <span className="flex flex-wrap items-center gap-2">
            {row.nomenclatureName}
            <span className="rounded-[var(--radius-sm)] border border-border bg-surface-2 px-1.5 font-mono text-[0.6875rem] text-text-muted">
              {row.code}
            </span>
          </span>
        </Line>
        <Line icon={<MapPin size={14} aria-hidden />} label={t('usage.detailWhere')}>
          {row.zoneName === null ? row.storeroomName : `${row.storeroomName} · ${row.zoneName}`}
        </Line>
        {row.requisitionDocNo === null ? null : (
          <Line icon={<FileText size={14} aria-hidden />} label={t('usage.detailRequisition')}>
            {row.requisitionId === null || onOpenRequisition === undefined ? (
              row.requisitionDocNo
            ) : (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onOpenRequisition(row.requisitionId as string);
                }}
                className="text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent"
              >
                {row.requisitionDocNo}
              </button>
            )}
          </Line>
        )}
      </dl>

      {/* Izoh — xodimning O'Z so'zlari, shuning uchun iqtibos bo'lib turadi. */}
      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-text-muted">{t('usage.detailComment')}</span>
        <blockquote className="border-l-2 border-accent/40 pl-3 text-sm text-pretty text-text italic">
          {row.comment}
        </blockquote>
      </div>
    </div>
  </Dialog>
);

const Line = ({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
    <dt className="flex min-w-32 items-center gap-1.5 text-xs text-text-muted">
      {icon}
      {label}
    </dt>
    <dd className="text-sm text-text">{children}</dd>
  </div>
);
